import { i18n } from "../translate/i18n";
import {
  extractTemplateVariableIndexes,
  extractVariablesFromComponents,
  parseTemplateComponents,
} from "./templateVariables";

/**
 * Meta template submit rules (mirror Venttifybackbiz template-submit-validation.ts).
 * Returns issue codes + field ids for inline UI mapping.
 */

export function parseTemplateComponentsInput(raw) {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function variableEdgeIssue(text, field) {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) {
    return null;
  }

  if (/^\{\{\d+\}\}/.test(trimmed)) {
    return { code: "template_variable_at_start", field };
  }

  if (/\{\{\d+\}\}[.!?,;:]*$/.test(trimmed)) {
    return { code: "template_variable_at_end", field };
  }

  return null;
}

function collectComponentTexts(components) {
  const texts = [];
  for (const component of components) {
    if (!component || typeof component !== "object") continue;
    const type = String(component.type ?? "").toUpperCase();
    if (type === "BODY" && typeof component.text === "string") {
      texts.push({ field: "body", text: component.text });
    }
    if (type === "BUTTONS" && Array.isArray(component.buttons)) {
      component.buttons.forEach((button, index) => {
        if (button?.type === "URL" && typeof button.url === "string") {
          texts.push({ field: `button_url_${index}`, text: button.url });
        }
      });
    }
  }
  return texts;
}

function validateVariableExamples(components) {
  const indexes = extractVariablesFromComponents(components);
  if (indexes.length === 0) {
    return null;
  }

  for (const component of components) {
    if (!component || typeof component !== "object") continue;
    const type = String(component.type ?? "").toUpperCase();

    if (type === "BODY" && typeof component.text === "string") {
      const bodyIndexes = extractTemplateVariableIndexes(component.text);
      if (bodyIndexes.length === 0) {
        continue;
      }
      const samples = component.example?.body_text?.[0];
      const hasSamples =
        Array.isArray(samples) &&
        samples.length === bodyIndexes.length &&
        samples.every((sample) => String(sample ?? "").trim());
      if (!hasSamples) {
        return { code: "template_variable_examples_missing", field: "variableExamples" };
      }
    }

    if (type === "BUTTONS" && Array.isArray(component.buttons)) {
      const sampleUrls = component.example?.button_url;
      let sampleIndex = 0;
      for (const button of component.buttons) {
        if (String(button?.type ?? "").toUpperCase() !== "URL" || typeof button?.url !== "string") {
          continue;
        }
        if (extractTemplateVariableIndexes(button.url).length === 0) {
          continue;
        }
        const sampleUrl = Array.isArray(sampleUrls) ? sampleUrls[sampleIndex] : undefined;
        sampleIndex += 1;
        if (!String(sampleUrl ?? "").trim()) {
          return { code: "template_variable_examples_missing", field: "variableExamples" };
        }
      }
    }
  }

  return null;
}

function validateVariableSequence(components) {
  const indexes = new Set();
  for (const { text } of collectComponentTexts(components)) {
    extractTemplateVariableIndexes(text).forEach((index) => indexes.add(index));
  }
  if (indexes.size === 0) {
    return null;
  }
  const sorted = [...indexes].sort((a, b) => a - b);
  for (let expected = 1; expected <= sorted.length; expected += 1) {
    if (sorted[expected - 1] !== expected) {
      return { code: "template_variables_not_sequential", field: "body" };
    }
  }
  return null;
}

export function validateTemplateForMetaSubmit(rawComponents) {
  const components = parseTemplateComponents(parseTemplateComponentsInput(rawComponents));
  const issues = [];

  for (const component of components) {
    if (!component || typeof component !== "object") continue;
    const type = String(component.type ?? "").toUpperCase();

    const headerFormat = String(component.format ?? "TEXT").toUpperCase();
    if (type === "HEADER" && ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat)) {
      const example = component.example ?? {};
      const hasHandle =
        Array.isArray(example.header_handle) &&
        String(example.header_handle[0] ?? "").trim();
      const hasUrl =
        Array.isArray(example.header_url) && String(example.header_url[0] ?? "").trim();
      if (!hasHandle && !hasUrl) {
        issues.push({ code: "template_media_sample_required", field: "headerMedia" });
      }
    }

    if (type === "HEADER" && headerFormat === "TEXT") {
      if (
        typeof component.text === "string" &&
        extractTemplateVariableIndexes(component.text).length > 0
      ) {
        issues.push({ code: "template_header_variables_not_allowed", field: "header" });
      }
    }

    if (type === "BODY" && typeof component.text === "string") {
      const edge = variableEdgeIssue(component.text, "body");
      if (edge) issues.push(edge);
    }

    if (type === "BUTTONS" && Array.isArray(component.buttons)) {
      component.buttons.forEach((button, index) => {
        if (String(button?.type ?? "").toUpperCase() !== "URL" || typeof button?.url !== "string") {
          return;
        }
        const edge = variableEdgeIssue(button.url, `button_url_${index}`);
        if (edge) {
          issues.push({ ...edge, field: "buttons" });
        }
      });
    }
  }

  const sequenceIssue = validateVariableSequence(components);
  if (sequenceIssue) {
    issues.push(sequenceIssue);
  }

  const examplesIssue = validateVariableExamples(components);
  if (examplesIssue) {
    issues.push(examplesIssue);
  }

  return issues;
}

export function translateTemplateSubmitIssue(issue) {
  if (!issue?.code) return "";
  const key = `whatsappTemplates.validation.${issue.code}`;
  return i18n.exists(key) ? i18n.t(key) : issue.code;
}

export function translateTemplateSubmitIssues(issues) {
  return (issues ?? []).map(translateTemplateSubmitIssue).filter(Boolean);
}

export function getTemplateFieldIssueMessage(issues, field) {
  const match = (issues ?? []).find((issue) => issue.field === field);
  return match ? translateTemplateSubmitIssue(match) : "";
}

/** Info-only hints (do not block submit). */
export function getTemplateSubmitHints({ language }) {
  const hints = [];
  const trimmed = String(language ?? "").trim();
  if (trimmed === "es") {
    hints.push(i18n.t("whatsappTemplates.validation.language_normalized_es"));
  }
  if (trimmed === "en") {
    hints.push(i18n.t("whatsappTemplates.validation.language_normalized_en"));
  }
  if (trimmed === "pt") {
    hints.push(i18n.t("whatsappTemplates.validation.language_normalized_pt"));
  }
  return hints;
}

export function validateTemplateRowBeforeSubmit(template) {
  const issues = validateTemplateForMetaSubmit(template?.components);
  return {
    ok: issues.length === 0,
    issues,
    messages: translateTemplateSubmitIssues(issues),
  };
}
